/**
 * @fileoverview Invoice Summary Cards Component
 * @task B.INV.5 - Accounts Receivable invoice screens
 * @theme Polished Corporate - Teal accents, refined shadows
 *
 * Displays summary metrics for AR invoices
 */

import { cn } from '@lib/utils';
import { useMarket } from '@contexts/MarketContext';

export interface InvoiceSummaryData {
  totalOutstanding: number;
  expectedThisMonth: number;
  overdue: number;
  collectedThisMonth: number;
  overdueCount: number;
  expectedCount: number;
}

interface InvoiceSummaryCardsProps {
  data: InvoiceSummaryData;
  isLoading?: boolean;
  isBrazil?: boolean;
  formatCurrency?: (amount: number) => string;
}

interface SummaryCardProps {
  title: string;
  value: string;
  subtitle?: string | undefined;
  icon: JSX.Element;
  trend?: { value: string; direction: 'up' | 'down' } | undefined;
  colorClass: string;
  isLoading?: boolean | undefined;
}

function SummaryCard({ title, value, subtitle, icon, trend, colorClass, isLoading }: SummaryCardProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="rounded-xl bg-white p-4 sm:p-6 shadow-card animate-pulse">
        <div className="flex items-center justify-between">
          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-gray-100" />
          <div className="h-4 w-12 sm:w-16 rounded-lg bg-gray-100" />
        </div>
        <div className="mt-3 sm:mt-4">
          <div className="h-3 w-20 sm:w-24 rounded-lg bg-gray-100" />
          <div className="mt-2 h-6 sm:h-8 w-24 sm:w-32 rounded-lg bg-gray-100" />
          <div className="mt-1 h-3 w-16 sm:w-20 rounded-lg bg-gray-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white p-4 sm:p-6 shadow-card transition-all hover:shadow-card-hover overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <div className={cn('inline-flex rounded-lg sm:rounded-xl p-2 sm:p-2.5 flex-shrink-0', colorClass)}>
          {icon}
        </div>
        {trend && (
          <div className={cn(
            'flex items-center gap-1 text-xs sm:text-sm font-medium flex-shrink-0',
            trend.direction === 'up' ? 'text-emerald-600' : 'text-red-500'
          )}>
            <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={trend.direction === 'up' ? 'M5 10l7-7m0 0l7 7m-7-7v18' : 'M19 14l-7 7m0 0l-7-7m7 7V3'}
              />
            </svg>
            {trend.value}
          </div>
        )}
      </div>
      <div className="mt-3 sm:mt-4 min-w-0 overflow-hidden">
        <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">{title}</p>
        <p className="mt-1 text-lg sm:text-2xl font-bold tracking-tight text-gray-900 truncate" title={value}>{value}</p>
        {subtitle && <p className="mt-0.5 text-[10px] sm:text-xs text-gray-400 truncate">{subtitle}</p>}
      </div>
    </div>
  );
}

export function InvoiceSummaryCards({ data, isLoading, isBrazil: isBrazilProp, formatCurrency: formatCurrencyProp }: InvoiceSummaryCardsProps): JSX.Element {
  const marketContext = useMarket();
  const isBrazil = isBrazilProp ?? marketContext.isBrazil;
  const formatCurrency = formatCurrencyProp ?? marketContext.formatCurrency;

  const getInvoiceLabel = (count: number) => {
    if (isBrazil) {
      return count !== 1 ? 'NF-es' : 'NF-e';
    }
    return count !== 1 ? 'invoices' : 'invoice';
  };

  const cards: Omit<SummaryCardProps, 'isLoading'>[] = [
    {
      title: isBrazil ? 'Total Pendente' : 'Total Outstanding',
      value: formatCurrency(data.totalOutstanding),
      subtitle: isBrazil ? 'Todas as NF-es não pagas' : 'All unpaid invoices',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      colorClass: 'bg-teal-50 text-teal-600',
    },
    {
      title: isBrazil ? 'Previsto Este Mês' : 'Expected This Month',
      value: formatCurrency(data.expectedThisMonth),
      subtitle: `${data.expectedCount} ${getInvoiceLabel(data.expectedCount)} ${isBrazil ? 'a vencer' : 'due'}`,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      trend: { value: '+12.5%', direction: 'up' },
      colorClass: 'bg-cyan-50 text-cyan-600',
    },
    {
      title: isBrazil ? 'Vencido' : 'Overdue',
      value: formatCurrency(data.overdue),
      subtitle: `${data.overdueCount} ${getInvoiceLabel(data.overdueCount)} ${isBrazil ? 'vencidas' : 'past due'}`,
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      colorClass: 'bg-red-50 text-red-600',
    },
    {
      title: isBrazil ? 'Recebido Este Mês' : 'Collected This Month',
      value: formatCurrency(data.collectedThisMonth),
      subtitle: isBrazil ? 'Pagamentos recebidos' : 'Payments received',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      trend: { value: '+28.4%', direction: 'up' },
      colorClass: 'bg-emerald-50 text-emerald-600',
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <SummaryCard key={card.title} {...card} isLoading={isLoading} />
      ))}
    </div>
  );
}
